import React, { useMemo } from 'react';
import { getReportDueDate } from '../reportDueDate'
import clsx from 'clsx';
import Lottie from 'lottie-react';
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json';
import dayjs from 'dayjs';
import traslation from '../../../components/modules/ui/traslation/traslationAdministracion.json';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const isCreditNote = (nombre, status) => {
  const nf = String(nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')

  return (
    nf === '61' ||
    nf === 'nce' ||
    (nf.includes('nota') && nf.includes('credito')) ||
    Boolean(extractAnnulledFolio(status))
  )
}

const extractAnnulledFolio = status => {
  const match = String(status || '')
    .toUpperCase()
    .match(/ANULA\s+FOLIO\s+(\d+)/)

  return match?.[1] || null
}

const extractCancelledByCreditNoteFolio = status => {
  const match = String(status || '')
    .toUpperCase()
    .match(/ANULAD[OA]\s+POR\s+NC\s+(\d+)/)

  return match?.[1] || null
}

export default function TableMovimientos({ 
  data = [], 
  className,
  lastElementRef, 
  hasNextPage, 
  isFetchingNextPage
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // ——— HELPER: Limpieza y parseo de montos ———
  const parseMonto = val => {
    if (typeof val === 'string') {
      const clean = val.replace(/\./g, '').replace(/,/g, '.');
      return Number(clean) || 0;
    }
    return Number(val) || 0;
  };

  const formatCLP = value =>
    value.toLocaleString('es-CL', { minimumFractionDigits: 0 });

  // ——— AGRUPAMIENTO POR RUT ———
  const groupedByRut = useMemo(() => {
    const map = new Map();

    data.forEach(mov => {
      const {
        rut,
        razonSocial,
        nombreFolio,
        numeroFolio,
        haber: haberValue = 0,
        debe: debeValue = 0,
        status = '',
        fecha,
        fechaVencimiento,
        linkage,
      } = mov;

      const statusStr = String(status).toUpperCase();
      const isNC = isCreditNote(nombreFolio, status);
      const folioAnulado = isNC ? extractAnnulledFolio(status) : null;
      const isAnulado = statusStr.startsWith('ANUL');

      // Suma de linkages
      const linkageSum = Array.isArray(linkage)
        ? linkage.reduce(
            (s, { montoRelacionado }) => s + parseMonto(montoRelacionado),
            0
          )
        : 0;

      // Definición de monto y pago
      const montoRow = isNC ? 0 : haberValue;
      const pagoRow = isNC ? parseMonto(debeValue) : linkageSum;

      // Saldo DOCUMENTO = monto - pago
      const auxiliarRow = montoRow - pagoRow;

      // Formateo de fechas
      const fechaDoc = fecha ? dayjs(fecha).format('DD/MM/YYYY') : '';
      const { vencimiento: displayVencimiento, dias } = getReportDueDate(fechaVencimiento);

      // Estado de cobro
      let estadoCobro = '';
      if (isAnulado) {
        estadoCobro = status;
      } else if (auxiliarRow === 0 || status === 0) {
        estadoCobro = 'PAGADO';
      } else if (pagoRow > 0) {
        estadoCobro = 'PAGADO PARCIAL';
      } else {
        estadoCobro = 'POR PAGAR';
      }

      // Inicializar grupo
      if (!map.has(rut)) {
        map.set(rut, {
          rut,
          razonSocial,
          items: [],
          totalMonto: 0,
          totalPago: 0,
          totalAuxiliar: 0,
        });
      }
      const group = map.get(rut);

      // Agregar registro
      group.items.push({
        rut,
        razonSocial,
        nombreFolio,
        numeroFolio,
        isNC,
        folioAnulado,
        fechaDoc,
        fechaVencimiento: fechaVencimiento ? String(fechaVencimiento).slice(0, 10).split('-').reverse().join('/') : '',
        displayVencimiento,
        dias,
        montoTotal: montoRow,
        pago: pagoRow,
        pagoInformado: pagoRow,
        auxiliar: auxiliarRow,
        estadoCobro,
      });
    });

    map.forEach(group => {
      const documentosPorFolio = new Map(
        group.items
          .filter(item => !item.isNC)
          .map(item => [String(item.numeroFolio), item])
      )

      group.items = group.items.map(item => {
        const saldaEnGrupo = !item.isNC || item.folioAnulado === '0' || (() => {
          const documentoAnulado = documentosPorFolio.get(String(item.folioAnulado))
          const folioNCEnDocumento = extractCancelledByCreditNoteFolio(documentoAnulado?.estadoCobro)

          return Boolean(documentoAnulado && folioNCEnDocumento === String(item.numeroFolio))
        })()
        const pago = saldaEnGrupo ? item.pago : 0
        const auxiliar = item.montoTotal - pago

        return {
          ...item,
          saldaEnGrupo,
          pago,
          auxiliar,
        }
      })

      group.totalMonto = group.items.reduce((sum, item) => sum + item.montoTotal, 0)
      group.totalPago = group.items.reduce((sum, item) => sum + item.pago, 0)
      group.totalAuxiliar = group.items.reduce((sum, item) => sum + item.auxiliar, 0)
    })

    return Array.from(map.values());
  }, [data]);

  // ——— GRAND TOTALS ———
  const grandTotals = useMemo(
    () =>
      groupedByRut.reduce(
        (acc, g) => ({
          monto: acc.monto + g.totalMonto,
          pago: acc.pago + g.totalPago,
          aux: acc.aux + g.totalAuxiliar,
        }),
        { monto: 0, pago: 0, aux: 0 }
      ),
    [groupedByRut]
  );

  if (groupedByRut.length === 0) {
    return <div className="py-12 text-center text-muted font-sans">No hay datos</div>;
  }

  if (isMobile) {
    const renderMetric = (label, value) => (
      <div className="rounded-lg bg-surface/70 border border-border px-2 py-2 min-w-0">
        <div className="text-[11px] text-muted">{label}</div>
        <div className="text-sm font-semibold text-text break-words">{value}</div>
      </div>
    );

    return (
      <div className={clsx('space-y-3 font-sans text-text', className)}>
        {groupedByRut.map((g, gi) => (
          <React.Fragment key={gi}>
            <div className="rounded-2xl border border-border bg-surface/70 p-3">
              <div className="text-xs font-semibold text-text">
                {g.rut} {g.razonSocial}
              </div>
              <div className="mt-2">
                {renderMetric('Subtotal', formatCLP(g.totalMonto))}
              </div>
            </div>

            {g.items.map((mov, mi) => (
              <div key={`${gi}-${mi}`} className="rounded-2xl border border-border bg-surface p-3">
                <div className="text-xs font-semibold text-text">
                  {(traslation.documentosContables[mov.nombreFolio] || mov.nombreFolio)} {mov.numeroFolio}
                </div>
                <div className="mt-1 text-[11px] text-muted">
                  {mov.fechaDoc} {mov.displayVencimiento ? `· ${mov.displayVencimiento}` : ''} {mov.dias !== '' ? `· ${mov.dias} días` : ''}
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {renderMetric('Fecha vencimiento', mov.fechaVencimiento)}
                  {renderMetric('Monto', mov.montoTotal !== 0 ? formatCLP(mov.montoTotal) : '')}
                  {renderMetric('Monto saldado', mov.pagoInformado > 0 ? formatCLP(mov.pagoInformado) : '')}
                  {renderMetric('Saldo', mov.isNC && !mov.saldaEnGrupo ? '' : formatCLP(mov.auxiliar))}
                  {renderMetric('Estado', mov.estadoCobro)}
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}

        <div className="rounded-2xl border border-border bg-surface p-3">
          <div className="text-xs font-semibold text-text">Total</div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {renderMetric('Monto', formatCLP(grandTotals.monto))}
            {renderMetric('Monto saldado', formatCLP(grandTotals.pago))}
            {renderMetric('Saldo', formatCLP(grandTotals.aux))}
          </div>
        </div>

        {hasNextPage && (
          <div ref={lastElementRef} className="flex justify-center py-4 flex-shrink-0">
            {isFetchingNextPage && (<Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />)}
          </div>
        )}
      </div>
    );
  }

  const headers = [
    'RUT',
    'Razón Social',
    'Documento',
    'Folio',
    'Fecha',
    'Fecha vencimiento',
    'Vencimiento',
    'Días',
    'Monto Total',
    'Monto Saldado',
    'Saldo Documento',
    'Estado',
  ];

  return (
    <div className="h-full min-h-0">
      {/* TABLA PRINCIPAL */}
      <div
        className={`overflow-y-auto overflow-x-auto border border-border rounded bg-surface font-sans text-[11px] sm:text-xs text-text leading-5 ${className}`}
      >
        <table
          className="table-report-auto min-w-[1120px]"
          style={{ tableLayout: 'auto' }}
        >
          <thead className="sticky top-0 bg-surface">
            <tr>
              {headers.map(col => {
                const isRazonSocial = col === 'Razón Social'
                const isDocumento = col === 'Documento'
                const isMontoSaldado = col === 'Monto Saldado'
                const isSaldoDocumento = col === 'Saldo Documento'
                const isEstado = col === 'Estado'
                const allowWrap =
                  isRazonSocial || isDocumento || isMontoSaldado || isSaldoDocumento || isEstado
                const widthClass = isDocumento
                  ? 'w-[68px]'
                  : isRazonSocial
                    ? 'w-[130px]'
                    : isMontoSaldado
                      ? 'w-[92px]'
                      : isSaldoDocumento
                        ? 'w-[98px]'
                        : isEstado
                          ? 'w-[95px]'
                          : ''
                return (
                  <th
                    key={col}
                    className={`table-report-th text-center leading-tight ${widthClass} ${
                      allowWrap ? 'whitespace-normal break-words' : 'whitespace-nowrap'
                    }`}
                  >
                    {col}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {groupedByRut.map((g, gi) => (
              <React.Fragment key={gi}>
                {g.items.map((mov, mi) => (
                  <tr key={mi} className={clsx('table-report-row')}>
                    <td className="table-report-td text-center whitespace-nowrap">{mov.rut}</td>
                    <td className="table-report-td text-center max-w-[130px] whitespace-normal break-words leading-tight">
                      {mov.razonSocial}
                    </td>
                    <td className="table-report-td text-center">
                      <span className="inline-block max-w-[68px] whitespace-normal break-all leading-tight">
                        {traslation.documentosContables[mov.nombreFolio] ||
                          mov.nombreFolio}
                      </span>
                    </td>
                    <td className="table-report-td text-center whitespace-nowrap">{mov.numeroFolio}</td>
                    <td className="table-report-td text-center whitespace-nowrap">{mov.fechaDoc}</td>
                    <td className="table-report-td text-center whitespace-nowrap">{mov.fechaVencimiento}</td>
                    <td className="table-report-td text-center">
                      {mov.displayVencimiento}
                    </td>
                    <td className="table-report-td text-center whitespace-nowrap">{mov.dias}</td>
                    <td className="table-report-td text-center whitespace-nowrap">
                      {mov.montoTotal !== 0 ? formatCLP(mov.montoTotal) : ''}
                    </td>
                    <td className="table-report-td text-center whitespace-nowrap">
                      {mov.pagoInformado > 0 ? formatCLP(mov.pagoInformado) : ''}
                    </td>
                    <td className="table-report-td text-center whitespace-nowrap">
                      {mov.isNC && !mov.saldaEnGrupo ? '' : formatCLP(mov.auxiliar)}
                    </td>
                    <td className="table-report-td text-center whitespace-normal break-words leading-tight">
                      {mov.estadoCobro}
                    </td>
                  </tr>
                ))}
                <tr className="table-report-section">
                  <td colSpan={6} className="table-report-td text-left">
                    Subtotal
                  </td>
                  <td className="table-report-td text-center" />
                  <td className="table-report-td text-center" />
                  <td className="table-report-td text-center">
                    {formatCLP(g.totalMonto)}
                  </td>
                  <td className="table-report-td text-center">
                    {formatCLP(g.totalPago)}
                  </td>
                  <td className="table-report-td text-center">
                    {formatCLP(g.totalAuxiliar)}
                  </td>
                  <td className="table-report-td text-center" />
                </tr>
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="table-report-total">
              <td colSpan={6} className="table-report-td text-left">
                TOTAL
              </td>
              <td className="table-report-td text-center" />
              <td className="table-report-td text-center" />
              <td className="table-report-td text-center">
                {formatCLP(grandTotals.monto)}
              </td>
              <td className="table-report-td text-center">
                {formatCLP(grandTotals.pago)}
              </td>
              <td className="table-report-td text-center">
                {formatCLP(grandTotals.aux)}
              </td>
              <td className="table-report-td text-center" />
            </tr>
          </tfoot>
        </table>

        {/* Infinite scroll trigger */}
        {hasNextPage && (
          <div ref={lastElementRef} className="flex justify-center py-4 flex-shrink-0">
            {isFetchingNextPage && (<Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />)}
          </div>
        )}
      </div>
    </div>
  );
}
